import { useCallback, useEffect, useMemo, useState } from 'react'
import { keccak256 } from 'ethers'
import routes, { links } from '../../../router/routes'
import {
  useGetMemecoinPrimePickQuery,
  useLazyGetMemecoinsHeldQuery,
  useLazyGetMemecoinsQuery,
} from '../../../modules/memecoins/api'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectMemecoinById,
  selectMemecoins,
} from '../../../modules/memecoins/selectors'
import { appendMemecoin, setMemecoins } from '../../../modules/memecoins/slice'
import styled from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import { Link } from 'react-router-dom'
import {
  channels,
  useWebSocketContext,
} from '../../../contexts/WebSocketContext'
import LaunchCard, {
  RowsLayout,
} from '../../../shared_components/Library/Cards/LaunchCard'
import {
  IRootReducer,
  Memecoin,
  TransactionEvent,
} from '../../../types/reducerTypes'
import {
  bigIntWeiToEthNumber,
  getCardLevelTokenValues,
} from '../useTokenContractValues'
import Star from './Star'
import { P, Span } from '../../../shared_components/Library/Typeography'
import { amountToWei } from '../../../utils/amount'
import { RoundedTextInput } from '../../../shared_components/TextInput'
import { KIRA_ADDRESS, hotfixKiraCoin } from '../TokenPage'
import { pagePaddingCssRules } from '../../../shared_components/Header/styles'
import { TabItem } from '../../../shared_components/Tab'
import { selectMemecoinsSearchQuery } from '../../../modules/miscUi/selectors'
import SkeletonLoader from '../../../shared_components/Library/Cards/LaunchCard/skeleton'
import { current } from '@reduxjs/toolkit'
import { useDebounce } from '@uidotdev/usehooks'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { useShouldShowForThreshold } from '../../../theme'

enum TokenListFilter {
  New = 'New',
  Trending = 'Trending',
  Top = 'Top',
  Graduated = 'Graduated',
  Held = 'Held',
}

export const useMemecoins = ({
  filter,
  search,
}: {
  filter?: TokenListFilter
  search: string
}) => {
  const [offset, setOffset] = useState(0)
  const [
    fetchMemecoins,
    { isLoading: isLoadingMemecoins, isFetching: isFetchingMemecoins },
  ] = useLazyGetMemecoinsQuery()
  const [
    fetchMemecoinsHeld,
    { isLoading: isLoadingHeldMemecoins, isFetching: isFetchingHeldMemecoins },
  ] = useLazyGetMemecoinsHeldQuery()
  const dispatch = useDispatch()
  const memecoins = useSelector(selectMemecoins)

  const isAtleastExpandedPlus = useShouldShowForThreshold('expandedPlus')

  const limit = isAtleastExpandedPlus ? 50 : 25

  const onLoadMore = useCallback(() => {
    const nextOffset = offset + limit
    setOffset(nextOffset)
  }, [limit, offset])

  const handleFetchMemecoins = useCallback(async () => {
    try {
      const {
        memecoins,
        meta: { total },
      } = await fetchMemecoins({
        offset,
        limit,
        filter: filter.toLocaleLowerCase(),
        search,
      }).unwrap()

      const memecoinsWithHotfix = memecoins.map((coin) => {
        if (coin.poolAddress === KIRA_ADDRESS) {
          return hotfixKiraCoin(coin)
        }
        return coin
      })
      dispatch(
        setMemecoins({
          memecoins: memecoinsWithHotfix,
          filter: filter.toLocaleLowerCase(),
        }),
      )
      setTotal(total)
    } catch (e) {
      console.error(e)
    }
  }, [dispatch, fetchMemecoins, filter, search, offset])

  const { subscribe, unsubscribe } = useWebSocketContext()
  const [transaction, setTransaction] = useState<TransactionEvent | undefined>()
  const [heldMemecoins, setHeldMemecoins] = useState([])
  const [total, setTotal] = useState(0)

  useEffect(() => {
    const channel = channels.poolCreated()

    subscribe('tokens-list', channel, (message) => {
      dispatch(appendMemecoin(message.data))
    })

    return () => {
      unsubscribe('tokens-list', channel)
    }
  }, [dispatch, subscribe, unsubscribe])

  useEffect(() => {
    async function fetchHeldMemecoins() {
      try {
        const { memecoinsHeld, total } = await fetchMemecoinsHeld({
          offset,
          limit,
          search,
        }).unwrap()

        const memecoinsWithHotfix = memecoinsHeld.map((coin) => {
          if (coin.poolAddress === KIRA_ADDRESS) {
            return hotfixKiraCoin(coin)
          }
          return coin
        })

        setTotal(total)

        if (offset === 0) {
          setHeldMemecoins(memecoinsWithHotfix)
          return
        }

        setHeldMemecoins((prev) => [...prev, ...memecoinsWithHotfix])
      } catch (e) {
        console.error(e)
        setTotal(0)
      }
    }

    if (filter === TokenListFilter.Held) {
      fetchHeldMemecoins()
    } else {
      handleFetchMemecoins()
    }
  }, [filter, search, offset])

  useEffect(() => {
    const transactions = channels.poolTransactions()

    subscribe('trending-pools-list', transactions, (message) => {
      setTransaction(message.data)
    })

    return () => {
      unsubscribe('trending-pools-list', transactions)
    }
  }, [subscribe, unsubscribe])

  const graduatedMemecoins = useMemo(() => {
    return memecoins.filter((memecoin) => {
      const ethRaised = memecoin?.meta?.ethRaised?.value

      if (!ethRaised) return false

      const isFourEthPool = memecoin?.isFourEthPool ?? false
      const totalEthToRaise = BigInt(
        amountToWei(isFourEthPool ? '5' : '4').toNumber(),
      )
      const totalEthToRaiseDecimal = bigIntWeiToEthNumber(totalEthToRaise)
      const ethRaisedDecimal = bigIntWeiToEthNumber(BigInt(ethRaised))
      const percentageProgress =
        (ethRaisedDecimal / totalEthToRaiseDecimal) * 100
      const isSaleComplete = percentageProgress >= 100

      return isSaleComplete
    })
  }, [memecoins])

  const loadingStates = {
    isLoading: isLoadingMemecoins || isLoadingHeldMemecoins,
    isFetching: isFetchingMemecoins || isFetchingHeldMemecoins,
  }

  if (filter === TokenListFilter.Held) {
    const pools = heldMemecoins.map((pool) => pool.memecoin)

    return { memecoins: pools, total, onLoadMore, setOffset, ...loadingStates }
  }

  return { memecoins, total, onLoadMore, setOffset, ...loadingStates }
}

const TokensList = () => {
  const [activeFilter, setActiveFilter] = useState<TokenListFilter>(
    TokenListFilter.Trending,
  )
  const memecoinsSearchQuery = useSelector(selectMemecoinsSearchQuery)
  const debounchedSearch = useDebounce(memecoinsSearchQuery, 300)

  const { memecoins, isLoading, isFetching, onLoadMore, total, setOffset } =
    useMemecoins({
      filter: activeFilter ?? TokenListFilter.Trending,
      search: debounchedSearch,
    })

  const [showInitialSkeletonLoader, setShowInitialSkeletonLoader] =
    useState(true)

  useEffect(() => {
    if (showInitialSkeletonLoader && !isFetching && !isLoading) {
      setShowInitialSkeletonLoader(false)
    }
  }, [isFetching, isLoading])

  const filters = [
    { label: TokenListFilter.Trending, icon: '/img/icons/trending-pool.svg' },
    { label: TokenListFilter.Top, icon: '/img/icons/top-pool.svg' },
    { label: TokenListFilter.New, icon: '/img/icons/new-pool.svg' },
    { label: TokenListFilter.Graduated, icon: '/img/icons/graduated-pool.svg' },
    {
      label: TokenListFilter.Held,
      icon: '/img/icons/memecoins/coins-held.svg',
    },
  ]

  const filteredMemecoins = !!memecoinsSearchQuery.length
    ? memecoins.filter(
      (memecoin) =>
        memecoin.tokenName
          .toLowerCase()
          .includes(memecoinsSearchQuery.toLowerCase()) ||
        memecoin.tokenSymbol
          .toLowerCase()
          .includes(memecoinsSearchQuery.toLowerCase()) ||
        memecoin.description
          .toLowerCase()
          .includes(memecoinsSearchQuery.toLowerCase()),
    )
    : memecoins

  const showLoader =
    showInitialSkeletonLoader ||
    ((isFetching || isLoading) && memecoins.length === 0)

  const showLoadMoreButton =
    total > 0 && !showLoader && memecoins.length < total

  const handleActiveFilter = (filter: TokenListFilter) => {
    if (filter === activeFilter) return
    setActiveFilter(filter)
    setOffset(0)
    setShowInitialSkeletonLoader(true)
  }

  return (
    <Box dc padB="section">
      <Box dc jc>
        <ContentContainer>
          <Box jc ac g={20} padT={16}>
            {filters.map(({ label, icon }) => (
              <TabItem
                ac
                jc
                dc
                g={6}
                isActive={activeFilter === label}
                onClick={() => handleActiveFilter(label)}
                key={label}
              >
                <img src={icon} alt={label} />
                <P s="S" fw={activeFilter === label ? 'semibold' : 'regular'}>
                  {label}
                </P>
              </TabItem>
            ))}
          </Box>
          <RowsLayout>
            {!showLoader &&
              filteredMemecoins.map((token, index) => (
                <MemecoinCard key={token._id} memecoin={token} />
              ))}
            {showLoader &&
              Array.from({ length: 8 }).map((_, index) => (
                <SkeletonLoader key={index} />
              ))}
          </RowsLayout>
          {showLoadMoreButton && (
            <Box padT={32} jc>
              <ThemedButton
                $styleType="tertiary"
                onClick={onLoadMore}
                disabled={isFetching}
                w="124.4px"
              >
                {isFetching ? (
                  <Box ac jc alignSelf="center">
                    <AnimatedLoader onlyWhite size={24} />
                  </Box>
                ) : (
                  'Load More ...'
                )}
              </ThemedButton>
            </Box>
          )}
        </ContentContainer>
      </Box>
    </Box>
  )
}

export default TokensList

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  align-self: center;
  justify-content: center;
  ${pagePaddingCssRules}
`

export function MemecoinCard({
  memecoin,
  highlighted,
}: {
  memecoin: Memecoin
  highlighted?: boolean
}) {
  const { percentageProgress } = getCardLevelTokenValues(memecoin)
  const [pctChange, setPctChange] = useState(0)

  useEffect(() => {
    if (!!pctChange) return
    setPctChange(getTwentyFourHourPctChange(memecoin))
  }, [memecoin, pctChange])

  return (
    <LaunchCard
      memecoin={memecoin}
      twentyFourHourPctChange={pctChange}
      progress={percentageProgress}
      highlighted={highlighted}
    />
  )
}

const BASE_PRICE = 1855404267

export function getTwentyFourHourPctChange(memecoin: Memecoin) {
  const twentyFourHoursAgoPrice = memecoin?.meta?.price24HrsAgo?.value
  const currentPriceRegisteredAt = memecoin?.meta?.ethOutForOneToken?.updatedAt
  const currentPrice = memecoin?.meta?.ethOutForOneToken?.value

  let twentyFourHourPctChange = 0

  if (!!currentPrice) {
    const TWENTY_FOUR_HOURS_IN_MILLISECONDS = 24 * 60 * 60 * 1000
    let pctChange = 0
    if (
      !!twentyFourHoursAgoPrice &&
      twentyFourHoursAgoPrice !== '0' &&
      !!currentPriceRegisteredAt &&
      !!twentyFourHoursAgoPrice &&
      Date.now() - new Date(currentPriceRegisteredAt).getTime() <
      TWENTY_FOUR_HOURS_IN_MILLISECONDS
    ) {
      const previousPrice = Number(twentyFourHoursAgoPrice)
      pctChange = ((Number(currentPrice) - previousPrice) / previousPrice) * 100
    } else {
      pctChange = ((Number(currentPrice) - BASE_PRICE) / BASE_PRICE) * 100
    }
    const isPositive = pctChange > 0
    const isZero = pctChange === 0
    if (isZero) return 0
    const isSubOne = Math.abs(pctChange) < 1
    if (isSubOne) {
      const poolAddress = memecoin.poolAddress
      const min = 0.81
      const max = 1.50
      const deterministicFraction = deterministicRandomFromAddress(poolAddress)
      const random = deterministicFraction * (max - min) + min
      pctChange = isPositive ? random : -random
    }
    const unsignedPctChange = Number(Math.abs(pctChange).toFixed(2))
    twentyFourHourPctChange = isPositive
      ? unsignedPctChange
      : -unsignedPctChange
  }

  return twentyFourHourPctChange
}

function deterministicRandomFromAddress(address: string): number {
  const normalisedAddress = address.toLowerCase()
  const hash = keccak256(normalisedAddress)
  const hexSlice = hash.slice(2, 10)
  const intValue = parseInt(hexSlice, 16)
  const fraction = Number(`0.${String((intValue / 0xFFFFFFFF) * 1000).split('.')[1]}`)
  return fraction
}

